import { ChangeEvent, FC, PropsWithChildren, Ref, useContext } from "react";
import cx from "classnames";
import { TextInputContext } from "./textInputContext";

export type QESOptionsListProps = OptionsListProps;

export interface OptionsListOption {
  key: string;
  value: string;
}

interface OptionsListProps {
  matchedOptions: OptionsListOption[];
  nonMatchedOptions: OptionsListOption[];
  showUnmatched?: boolean;
  textIfNoMatch?: string;
  onOptionClick?: (option: OptionsListOption) => void;
  optionsListRef: Ref<HTMLDivElement>;
}

/**
 * A component meant to be nested as a child of QESTextInput that provides a styled, filterable list of options to the user.
 */
const QESOptionsList: FC<PropsWithChildren<QESOptionsListProps>> = ({
                                                                      matchedOptions,
                                                                      nonMatchedOptions,
                                                                      showUnmatched,
                                                                      textIfNoMatch,
                                                                      onOptionClick,
                                                                      optionsListRef
                                                                    }) => {
  const inputContext = useContext(TextInputContext);

  const handleOptionClick = (option: OptionsListOption) => {
    inputContext.onChange({ target: { value: option.value } } as ChangeEvent<HTMLInputElement>);
    if (onOptionClick) {
      onOptionClick(option);
    }

    inputContext.setFocused(false);
  }

  if (!inputContext.focused || !inputContext.optionsListOpen) {
    return null;
  }

  return (
    <div className="qes-text-input__options-dropdown" ref={optionsListRef}>
      {matchedOptions.map((option, index) => (
        <button
          key={option.key}
          onMouseDown={() => handleOptionClick(option)}
          className={cx({
            "highlighted": inputContext.highlightedIndex === index,
            "qes-button new-action-menu-item": true
          })}
        >{option.value}</button>
      ))}
      {!showUnmatched && matchedOptions.length === 0 && (
        <button disabled className="qes-button new-action-menu-item qes-text-input__options-dropdown-non-matching"
        >{textIfNoMatch || "No options found."}</button>
      )}
      {showUnmatched && (
        <>
          <div className="qes-text-input__options-dropdown-divider" />
          {nonMatchedOptions.map((option, index) => {
            const actualIndex = matchedOptions.length + index;
            return (
              <button
                key={option.key}
                onMouseDown={() => handleOptionClick(option)}
                className={cx({
                  "highlighted": inputContext.highlightedIndex === actualIndex,
                  "qes-button new-action-menu-item qes-text-input__options-dropdown-non-matching": true
                })}
              >{option.value}</button>
            );
          })}
        </>
      )}
    </div>
  )
}

export default QESOptionsList;